<script setup>
import { computed, ref, nextTick, watch } from 'vue'
import { Listbox, ListboxButton, ListboxLabel, ListboxOptions, ListboxOption } from '@headlessui/vue'

const props = defineProps({
  modelValue: { type: [String, Number, Object], default: null },
  options: { type: Array, required: true },
  placeholder: { type: String, default: 'Select option' },
  label: { type: String, default: ''},
  disabled: { type: Boolean, default: false },
  containerClass: { type: String, default: '' },
  labelClass: { type: String, default: 'controller-label text-xs' },
  buttonClass: { type: String, default: 'w-[294px]' },
  buttonIcon: { type: String, default: '' },
  buttonIconType: { type: String, default: 'svg' },
  buttonIconClass: { type: String, default: '' },
  optionsClass: { type: String, default: '' },
  selectedIconClass: { type: String, default: '' },
  optionValueKey: { type: String, default: 'value' },
  optionLabelKey: { type: String, default: 'label' },
  fallbackValueKey: { type: String, default: '' },
  isSimpleArray: { type: Boolean, default: false },
  error: { type: Boolean, default: false },
  hideChevron: { type: Boolean, default: false },
  showButtonContent: { type: Boolean, default: true },
  placement: { type: String, default: 'bottom' },
  isCapitalize: { type: Boolean, default: true },
  usePortal: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

const buttonRef = ref(null)
const dropdownStyle = ref({})

// Calculate fixed position for portal mode
const updateDropdownPosition = async () => {
  if (!props.usePortal || !buttonRef.value?.$el) return
  
  await nextTick()
  const rect = buttonRef.value.$el.getBoundingClientRect()
  
  if (props.placement === 'top') {
    dropdownStyle.value = {
      position: 'fixed',
      left: `${rect.left}px`,
      bottom: `${window.innerHeight - rect.top + 4}px`,
      width: `${rect.width}px`,
      zIndex: 9999,
    }
  } else {
    dropdownStyle.value = {
      position: 'fixed',
      left: `${rect.left}px`,
      top: `${rect.bottom + 4}px`,
      width: `${rect.width}px`,
      zIndex: 9999,
    }
  }
}

// Normalize options to handle both object arrays and simple arrays
const normalizedOptions = computed(() => {
  if (props.isSimpleArray) {
    return props.options.map(option => ({
      value: option,
      label: option
    }))
  }
  return props.options
})

// Get display value for the button
const displayValue = computed(() => {
  if (props.modelValue === null || props.modelValue === undefined) return null
  
  if (props.isSimpleArray) {
    return props.modelValue
  }

  const option = props.options.find(opt => getOptionValue(opt) === props.modelValue)
  return option ? getOptionLabel(option) : Object.keys(props.modelValue).length ? getOptionLabel(props.modelValue) : props.modelValue

  //return option ? getOptionLabel(option) : typeof props.modelValue === 'object' ? props.modelValue[props.optionValueKey] : props.modelValue
})

// Helper functions for option value and label extraction
const getOptionValue = (option) => {
  if (props.isSimpleArray) return option
  const primaryValue = option[props.optionValueKey]
  if (primaryValue !== undefined && primaryValue !== null) {
    return primaryValue
  }
  // Use fallback if primary is undefined/null
  if (props.fallbackValueKey) {
    return option[props.fallbackValueKey] ?? option
  }
  return option
}

const getOptionLabel = (option) => {
  if (props.isSimpleArray) return option
  return option[props.optionLabelKey] ?? option
}

// Handle value updates
const handleUpdate = (value) => {
  emit('update:modelValue', value)
}
</script>
<template>
  <Listbox 
    :model-value="modelValue" 
    @update:model-value="handleUpdate" 
    v-slot="{ open }"
  >
    <div class="relative" :class="[containerClass, { '!z-50': open }]" v-bind="$attrs">
      <ListboxLabel v-if="label" :class="labelClass">{{ label }}</ListboxLabel>
      <ListboxButton
        ref="buttonRef"
        class="
          relative cursor-default rounded-lg bg-gray-50 py-2 px-4 text-left border flex items-center
          gap-x-2 text-sm transition duration-300 focus:outline-none focus-visible:border-tint-1
          focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-50 focus-visible:ring-offset-2
          focus-visible:ring-offset-blue-300
        "
        :class="[buttonClass, { 'border-tint-1': open }, { 'border-red-500 bg-red-50': error }]"
        :disabled="disabled"
        @click="updateDropdownPosition"
      >
        <slot name="button-content" v-if="showButtonContent">
          <span v-if="buttonIcon && buttonIconType === 'svg'" :class="buttonIconClass" v-html="buttonIcon" />
          <span 
            v-text="displayValue || placeholder" 
            class="transition duration-300 flex-1 popup" 
            :class="{ 'text-red-700': error, 'text-gray-500': !displayValue && !error, 'capitalize': isCapitalize }"
          />
        </slot>
        
        <Icon
          v-if="!hideChevron"
          icon="heroicons:chevron-down-16-solid"
          class="text-base text-gray-500 pointer-events-none transition duration-300 popup"
          :class="{ 'rotate-180': open }"
        />
      </ListboxButton>
      
      <Teleport to="body" :disabled="!usePortal">
        <transition name="fade">
          <ListboxOptions
            class="
              max-h-96 min-w-[10rem] overflow-auto rounded-lg bg-white py-2
              text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none popup
            "
            :class="[
              optionsClass,
              usePortal ? '' : 'absolute right-0 w-full',
              !usePortal && placement === 'bottom' ? 'top-full mt-1' : '',
              !usePortal && placement === 'top' ? 'bottom-full mb-1' : '',
              placement === 'bottom' ? 'top-full mt-1' : 'bottom-full mb-1',
            { '!overflow-visible !bg-transparent !shadow-none !ring-0 !py-0 !rounded-none': $slots['custom-options'] }
            ]"
            :style="usePortal ? dropdownStyle : {}"
          >

            <!-- Custom options slot for advanced use cases (e.g., nested dropdowns) -->
            <slot 
              v-if="$slots['custom-options']" 
              name="custom-options" 
              :options="normalizedOptions"
              :getOptionValue="getOptionValue"
              :getOptionLabel="getOptionLabel"
              :modelValue="modelValue"
            />

            <template v-else>
              <ListboxOption
                v-slot="{ active, selected }"
                v-for="option in normalizedOptions"
                :value="getOptionValue(option)"
                :key="getOptionValue(option)"
                as="template"
              >
                <li
                  class="
                    cursor-pointer select-none py-2 pl-3 pr-3 gap-x-2 flex items-center justify-between transition duration-300 popup
                  "
                  :class="{ 'bg-gray-100': active }"
                >
                  <slot name="option-content" :option="option" :selected="selected" :active="active">
                    <span v-if="option.icon" v-html="option.icon" />
                    <span
                      class="block truncate popup"
                      :class="{ 'font-medium text-tint-1' : selected }"
                      v-text="getOptionLabel(option)"
                    />
                    <Icon
                      v-if="selected"
                      icon="heroicons:check-20-solid"
                      class="text-xl text-tint-1 popup"
                      :class="selectedIconClass"
                      aria-hidden="true"
                    />
                  </slot>
  
                  
                </li>
              </ListboxOption>
            </template>
          </ListboxOptions>
        </transition>
      </Teleport>
    </div>
  </Listbox>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style> 